import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  MapPin,
  Users,
  Clock,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Filter,
  User,
  Navigation,
  Wifi,
  WifiOff,
  Building2,
  Search,
  History,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Popup, Circle, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { format } from 'date-fns';
import { fmtTz } from "../lib/time";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons
const employeeIconActive = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #059669; border: 3px solid white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const employeeIconOnline = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #eab308; border: 3px solid white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const employeeIconOffline = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #6b7280; border: 3px solid white; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const employeeIconWarning = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #9ca3af; border: 3px solid #f59e0b; border-radius: 50%; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
  popupAnchor: [0, -16]
});

const siteIcon = new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background: #3b82f6; border: 3px solid white; border-radius: 8px; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
  </div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
  popupAnchor: [0, -36]
});

// Map center component to handle flying to selected employee
function MapController({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.flyTo(center, zoom || 15, { duration: 1 });
    }
  }, [center, zoom, map]);
  return null;
}

export default function AdminLiveTracking() {
  const { authAxios } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // History state
  const [activeTab, setActiveTab] = useState('live');
  const [historyEmployee, setHistoryEmployee] = useState('');
  const [historyStartDate, setHistoryStartDate] = useState(fmtTz(new Date(), 'yyyy-MM-dd'));
  const [historyEndDate, setHistoryEndDate] = useState(fmtTz(new Date(), 'yyyy-MM-dd'));
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [empRes, sitesRes, countRes] = await Promise.all([
        authAxios.get('/location/admin/all-current'),
        authAxios.get('/location/admin/sites'),
        authAxios.get('/location/admin/online-count')
      ]);
      setEmployees(empRes.data);
      setSites(sitesRes.data);
      setOnlineCount(countRes.data.online_count);
      setLastRefresh(new Date());
      
      // Fetch all employees separately for history dropdown (non-critical)
      try {
        const allEmpRes = await authAxios.get('/employees');
        setAllEmployees(allEmpRes.data);
      } catch (e) {
        // Use current location data for employee list
        setAllEmployees(empRes.data.map(e => ({ id: e.employee_id, full_name: e.employee_name })));
      }
    } catch (error) {
      console.error('Failed to fetch location data:', error);
      toast.error('Failed to load tracking data');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  const fetchHistory = useCallback(async () => {
    if (!historyEmployee || !historyStartDate) return;
    
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ start_date: historyStartDate, end_date: historyEndDate || historyStartDate });
      const res = await authAxios.get(`/location/admin/employee/${historyEmployee}/history?${params}`);
      setHistoryData(res.data);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load location history');
    } finally {
      setHistoryLoading(false);
    }
  }, [authAxios, historyEmployee, historyStartDate, historyEndDate]);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const isValidCoord = (lat, lng) => {
    return lat != null && lng != null && 
           isFinite(lat) && isFinite(lng) &&
           Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
  };

  // Filter employees
  const filteredEmployees = employees.filter(emp => {
    // Status filter
    if (filter === 'clocked_in' && emp.status !== 'clocked_in') return false;
    if (filter === 'online' && emp.status !== 'online') return false;
    if (filter === 'offline' && emp.status !== 'offline' && !emp.signal_lost) return false;
    if (filter === 'signal_lost' && !emp.signal_lost) return false;

    // Site filter
    if (siteFilter !== 'all' && emp.assigned_site_id !== siteFilter) return false;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!emp.employee_name?.toLowerCase().includes(query) &&
          !emp.employee_code?.toLowerCase().includes(query)) return false;
    }

    return true;
  });

  const getEmployeeIcon = (emp) => {
    if (emp.signal_lost) return employeeIconWarning;
    if (emp.status === 'clocked_in') return employeeIconActive;
    if (emp.status === 'online') return employeeIconOnline;
    return employeeIconOffline;
  };

  const getStatusBadge = (emp) => {
    if (emp.signal_lost) {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><AlertTriangle className="w-3 h-3 mr-1" />Signal Lost</Badge>;
    }
    if (emp.status === 'clocked_in') {
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><Wifi className="w-3 h-3 mr-1" />Clocked In</Badge>;
    }
    if (emp.status === 'online') {
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Wifi className="w-3 h-3 mr-1" />Online</Badge>;
    }
    return <Badge className="bg-stone-100 text-stone-600 border-stone-200"><WifiOff className="w-3 h-3 mr-1" />Offline</Badge>;
  };

  const formatMinutesAgo = (minutes) => {
    if (!minutes && minutes !== 0) return 'Unknown';
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${Math.round(minutes)} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${Math.round(minutes % 60)}m ago`;
  };

  const handleEmployeeClick = (emp) => {
    setSelectedEmployee(emp);
    if (emp.latitude && emp.longitude) {
      setMapCenter([emp.latitude, emp.longitude]);
    }
  };

  // Calculate default map center based on employee/site locations
  const getDefaultCenter = () => {
    const allPoints = [
      ...employees.filter(e => isValidCoord(e.latitude, e.longitude)).map(e => [e.latitude, e.longitude]),
      ...sites.filter(s => isValidCoord(s.latitude, s.longitude)).map(s => [s.latitude, s.longitude])
    ];
    if (allPoints.length > 0) {
      const avgLat = allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length;
      const avgLon = allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length;
      return [avgLat, avgLon];
    }
    return [49.2827, -123.1207]; // Default to Vancouver
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col" data-testid="live-tracking">
      {/* Header */}
      <div className="px-4 py-4 border-b bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Navigation className="w-6 h-6 text-emerald-600" />
              <h1 className="text-2xl font-bold text-stone-900 font-heading">Live Tracking</h1>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 text-lg px-3 py-1">
              <Users className="w-4 h-4 mr-2" />
              {onlineCount} Online
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="live" className="flex items-center gap-1">
                  <Navigation className="w-4 h-4" />
                  Live Map
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-1">
                  <History className="w-4 h-4" />
                  History
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>

        {/* Filters for Live View */}
        {activeTab === 'live' && (
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-stone-400" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[150px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  <SelectItem value="clocked_in">Clocked In</SelectItem>
                  <SelectItem value="online">Online Only</SelectItem>
                  <SelectItem value="offline">Offline</SelectItem>
                  <SelectItem value="signal_lost">Signal Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Select value={siteFilter} onValueChange={setSiteFilter}>
              <SelectTrigger className="w-[180px] h-9">
                <SelectValue placeholder="Filter by Site" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sites</SelectItem>
                {sites.map(site => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-stone-500">Updated: {fmtTz(lastRefresh, 'h:mm:ss a')}</span>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        )}

        {/* Controls for History View */}
        {activeTab === 'history' && (
          <div className="flex flex-wrap items-center gap-3 mt-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-500">Employee:</Label>
              <Select value={historyEmployee} onValueChange={setHistoryEmployee}>
                <SelectTrigger className="w-[200px] h-9" data-testid="history-employee-select">
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {allEmployees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-500">From:</Label>
              <Input
                type="date"
                value={historyStartDate}
                onChange={(e) => setHistoryStartDate(e.target.value)}
                className="w-[150px] h-9"
                data-testid="history-start-date"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-stone-500">To:</Label>
              <Input
                type="date"
                value={historyEndDate}
                onChange={(e) => setHistoryEndDate(e.target.value)}
                className="w-[150px] h-9"
                data-testid="history-end-date"
              />
            </div>
            <Button onClick={fetchHistory} disabled={!historyEmployee || historyLoading} data-testid="load-history-btn">
              {historyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
              Load History
            </Button>
          </div>
        )}
      </div>

      {/* Live View */}
      {activeTab === 'live' && (
        <div className="flex-1 flex overflow-hidden">
        {/* Map */}
        <div className="flex-1 relative">
          <MapContainer
            center={getDefaultCenter()}
            zoom={12}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            {mapCenter && <MapController center={mapCenter} zoom={16} />}

            {/* Site markers with geofence circles */}
            {sites.filter(s => isValidCoord(s.latitude, s.longitude)).map(site => (
              <React.Fragment key={site.id}>
                <Marker position={[site.latitude, site.longitude]} icon={siteIcon}>
                  <Popup>
                    <div className="min-w-[200px]">
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {site.name}
                      </h3>
                      {site.location && <p className="text-sm text-stone-500 mt-1">{site.location}</p>}
                      <div className="mt-2 text-sm">
                        <p>Geofence: {site.geofencing_enabled ? 
                          <span className="text-emerald-600">Enabled ({site.radius_meters}m)</span> : 
                          <span className="text-stone-400">Disabled</span>}
                        </p>
                      </div>
                    </div>
                  </Popup>
                </Marker>
                {site.geofencing_enabled && (
                  <Circle
                    center={[site.latitude, site.longitude]}
                    radius={site.radius_meters || 150}
                    pathOptions={{
                      color: '#059669',
                      fillColor: '#059669',
                      fillOpacity: 0.1,
                      weight: 2
                    }}
                  />
                )}
              </React.Fragment>
            ))}

            {/* Employee markers */}
            {filteredEmployees.filter(e => isValidCoord(e.latitude, e.longitude)).map(emp => (
              <Marker
                key={emp.employee_id}
                position={[emp.latitude, emp.longitude]}
                icon={getEmployeeIcon(emp)}
              >
                <Popup>
                  <div className="min-w-[220px]">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold">{emp.employee_name}</h3>
                      {getStatusBadge(emp)}
                    </div>
                    {emp.employee_code && <p className="text-sm text-stone-500">{emp.employee_code}</p>}
                    {emp.assigned_site_name && (
                      <p className="text-sm mt-2">
                        <span className="text-stone-500">Assigned: </span>
                        <span className="font-medium">{emp.assigned_site_name}</span>
                      </p>
                    )}
                    <div className="mt-2 pt-2 border-t text-sm text-stone-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Last seen: {formatMinutesAgo(emp.minutes_since_update)}
                    </div>
                    {emp.accuracy_meters && (
                      <p className="text-xs text-stone-400 mt-1">
                        Accuracy: ±{Math.round(emp.accuracy_meters)}m
                      </p>
                    )}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>

          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[1000]">
            <p className="text-xs font-semibold text-stone-500 mb-2">LEGEND</p>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-600" />
                <span>Clocked In</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-amber-500" />
                <span>Online (not clocked in)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-stone-400" />
                <span>Offline</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-stone-300 border-2 border-amber-500" />
                <span>Signal Lost (5+ min)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-500" />
                <span>Job Site</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 bg-white border-l overflow-y-auto">
          <div className="p-4 border-b bg-stone-50">
            <h2 className="font-semibold text-stone-700">Employees ({filteredEmployees.length})</h2>
          </div>
          <div className="divide-y">
            {filteredEmployees.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No employees match your filters</p>
              </div>
            ) : (
              filteredEmployees.map(emp => (
                <div
                  key={emp.employee_id}
                  className={`p-4 hover:bg-stone-50 cursor-pointer transition-colors ${
                    selectedEmployee?.employee_id === emp.employee_id ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''
                  }`}
                  onClick={() => handleEmployeeClick(emp)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-stone-900">{emp.employee_name}</p>
                      {emp.employee_code && (
                        <p className="text-sm text-stone-500">{emp.employee_code}</p>
                      )}
                    </div>
                    {getStatusBadge(emp)}
                  </div>
                  {emp.assigned_site_name && (
                    <p className="text-sm text-stone-600 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {emp.assigned_site_name}
                    </p>
                  )}
                  <p className={`text-xs mt-2 flex items-center gap-1 ${
                    emp.signal_lost ? 'text-amber-600 font-medium' : 'text-stone-400'
                  }`}>
                    <Clock className="w-3 h-3" />
                    {emp.signal_lost && <AlertTriangle className="w-3 h-3" />}
                    {formatMinutesAgo(emp.minutes_since_update)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      )}

      {/* History View */}
      {activeTab === 'history' && (
        <div className="flex-1 flex overflow-hidden">
          {/* History Map */}
          <div className="flex-1 relative">
            {historyData && historyData.locations.length > 0 ? (
              <MapContainer
                center={[historyData.locations[0].latitude, historyData.locations[0].longitude]}
                zoom={14}
                style={{ height: '100%', width: '100%' }}
                className="z-0"
                key={`history-${historyEmployee}-${historyStartDate}-${historyEndDate}`}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                
                {/* Path Polyline */}
                <Polyline
                  positions={historyData.locations.map(loc => [loc.latitude, loc.longitude])}
                  pathOptions={{ color: '#059669', weight: 3, opacity: 0.8 }}
                />
                
                {/* Start marker */}
                <Marker position={[historyData.locations[0].latitude, historyData.locations[0].longitude]}>
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold text-emerald-600">Start</p>
                      <p>{fmtTz(historyData.locations[0].recorded_at, 'h:mm a')}</p>
                    </div>
                  </Popup>
                </Marker>
                
                {/* End marker */}
                {historyData.locations.length > 1 && (
                  <Marker position={[
                    historyData.locations[historyData.locations.length - 1].latitude,
                    historyData.locations[historyData.locations.length - 1].longitude
                  ]}>
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold text-rose-600">End</p>
                        <p>{fmtTz(historyData.locations[historyData.locations.length - 1].recorded_at, 'h:mm a')}</p>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            ) : (
              <div className="flex items-center justify-center h-full bg-stone-50">
                <div className="text-center">
                  <History className="w-16 h-16 mx-auto text-stone-300 mb-4" />
                  <p className="text-stone-500">
                    {historyLoading ? 'Loading history...' : 
                     historyData ? 'No location data for this date' : 
                     'Select an employee and date to view location history'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* History Sidebar - Location Table */}
          <div className="w-96 bg-white border-l overflow-y-auto">
            <div className="p-4 border-b bg-stone-50">
              <h2 className="font-semibold text-stone-700">
                {historyData ? (
                  <>
                    {historyData.employee.name}
                    <span className="text-sm font-normal text-stone-500 block mt-1">
                      {historyStartDate === historyEndDate 
                        ? fmtTz(historyStartDate, 'MMM d, yyyy')
                        : `${fmtTz(historyStartDate, 'MMM d')} - ${fmtTz(historyEndDate, 'MMM d, yyyy')}`
                      } ({historyData.total_points} points)
                    </span>
                  </>
                ) : 'Location History'}
              </h2>
            </div>
            
            {historyData && historyData.locations.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Time</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                      <TableHead className="text-xs">Speed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyData.locations.map((loc, idx) => (
                      <TableRow key={loc.id || idx} className="text-sm">
                        <TableCell className="py-2">
                          {fmtTz(loc.recorded_at, 'h:mm:ss a')}
                        </TableCell>
                        <TableCell className="py-2">
                          <Badge className={`text-xs ${
                            loc.status === 'clocked_in' ? 'bg-emerald-100 text-emerald-700' :
                            loc.status === 'online' ? 'bg-amber-100 text-amber-700' :
                            'bg-stone-100 text-stone-600'
                          }`}>
                            {loc.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 text-stone-500">
                          {loc.speed_kmh ? `${Math.round(loc.speed_kmh)} km/h` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-8 text-center text-stone-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No history data loaded</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
